/**
 * Price — Photo de profil optionnelle proposée à l'inscription.
 *
 * Comme la photo doit passer par l'endpoint `POST /price/me/profile-photo`
 * (authentifié), on doit soit :
 *   - l'uploader immédiatement après un auto-login (client),
 *   - la stocker en localStorage et l'envoyer au premier login réussi
 *     (chauffeur — pré-inscription en attente de validation admin).
 *
 * Ce module centralise :
 *   - la compression frontend (canvas → webp 400×400, ~150 KB),
 *   - la persistance temporaire (`price_pending_profile_photo`),
 *   - le flush automatique au premier login authentifié.
 */
import { api } from "@/lib/api";

const PENDING_KEY = "price_pending_profile_photo";
const MAX_BASE64_LEN = 800_000; // ~600 KB binary, backend limit

/**
 * Compresse un fichier image en data-URL WebP 400×400 (fallback JPEG).
 * Rejette si le rendu dépasse MAX_BASE64_LEN caractères.
 */
export function compressProfileImage(file) {
  return new Promise((resolve, reject) => {
    if (!file || !file.type?.startsWith?.("image/")) {
      reject(new Error("Fichier image invalide"));
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const target = 400;
        const canvas = document.createElement("canvas");
        canvas.width = target;
        canvas.height = target;
        const ctx = canvas.getContext("2d");
        const side = Math.min(img.width, img.height);
        const sx = (img.width - side) / 2;
        const sy = (img.height - side) / 2;
        ctx.drawImage(img, sx, sy, side, side, 0, 0, target, target);
        const dataUrl = canvas.toDataURL("image/webp", 0.75);
        if (dataUrl.length > MAX_BASE64_LEN) {
          reject(new Error("Photo trop lourde après compression"));
          return;
        }
        resolve(dataUrl);
      };
      img.onerror = () => reject(new Error("Image illisible"));
      img.src = reader.result;
    };
    reader.onerror = () => reject(new Error("Lecture fichier impossible"));
    reader.readAsDataURL(file);
  });
}

export function savePendingPhoto(dataUrl) {
  try { localStorage.setItem(PENDING_KEY, dataUrl); } catch (_) { /* ignore */ }
}

export function readPendingPhoto() {
  try { return localStorage.getItem(PENDING_KEY) || null; } catch (_) { return null; }
}

export function clearPendingPhoto() {
  try { localStorage.removeItem(PENDING_KEY); } catch (_) { /* ignore */ }
}

/**
 * À appeler quand une session est disponible (par ex. au premier
 * `useEffect` du dashboard). Envoie silencieusement la photo en attente
 * puis la retire du localStorage. Ne crash jamais — un échec réseau
 * conserve la photo pour un prochain essai.
 */
export async function flushPendingPhoto() {
  const dataUrl = readPendingPhoto();
  if (!dataUrl) return { flushed: false };
  try {
    await api.post("/price/me/profile-photo", { data_url: dataUrl });
    clearPendingPhoto();
    return { flushed: true };
  } catch (_) {
    return { flushed: false };
  }
}
